import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  Signal,
  untracked,
} from '@angular/core';
import {
  AnalysisAggregateRow,
  AnalysisRowType,
  AnalysisVersionGroupResponse,
} from '@api-response-types/analysis-version-group.response';
import { AnalysisDataSourceType } from '@api-types/enums/analysis-data-source-type.enum';
import { Measure } from '@api-types/enums/measure.enum';
import { GridViewManagerComponent } from '@components/table/grid-view-manager/grid-view-manager.component';
import { createGetMainMenuItems } from '@components/table/grid-view-manager/utils/get-main-menu-items';
import { ClientSideGridComponent } from '@components/table/grid/client-side-grid.component';
import {
  columnsToolPanelDef,
  filtersToolPanelDef,
} from '@components/table/grid/config/columns-tool-panel';
import {
  alphabeticalCompare,
  createNumberFilterParams,
  numericFilterColDef,
} from '@components/table/grid/config/filter-params';
import { GridModule } from '@components/table/grid/grid.module';
import { PmNoRowsOverlayComponent } from '@components/table/no-rows-overlay/pm-no-rows-overlay.component';
import { GridTooltipTranslationComponent } from '@components/table/grid/tooltip/grid-tooltip-translation/grid-tooltip-translation.component';
import { LocaleService } from '@core/services/locale.service';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { formatValueAsNumber } from '@core/utils/formatter-utility';
import { TwoDecimalPercentPipe } from '@core/utils/two-decimal-percent.pipe';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  FilterChangedEvent,
  FilterModel,
  GetMainMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GROUP_AUTO_COLUMN_ID,
  HeaderStyle,
  ISetFilterParams,
  PostSortRowsParams,
  RowClassParams,
  SideBarDef,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-enterprise';
import { GroupNameCellComponent } from 'demand-forecasting/analysis/components/analysis-table/group-name-cell/group-name-cell.component';
import { OpenForecastVersionCellComponent } from 'demand-forecasting/analysis/components/analysis-table/open-forecast-version-cell/open-forecast-version-cell.component';
import { AnalysisNavigationService } from 'demand-forecasting/analysis/services/analysis-navigation.service';
import { AnalysisContextService } from 'demand-forecasting/analysis/services/analysis-context.service';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import { AnalysisThresholdService } from 'demand-forecasting/analysis/services/analysis-threshold.service';
import {
  createMeasureVisibility,
  getMeasureTooltipKey,
  getSelectedMeasureColumns,
  SelectedMeasureColumn,
  toAccuracyValue,
} from 'demand-forecasting/analysis/services/measure-display';
import {
  selectAnalysisFilter,
  selectAnalysisHierarchy,
  selectAnalysisTableFilterByQueryParam,
} from 'demand-forecasting/store/router-state/router.selectors';

@Component({
  selector: 'pm-analysis-table',
  templateUrl: './analysis-table.component.html',
  imports: [TranslateModule, GridModule, ClientSideGridComponent],
  providers: [TwoDecimalPercentPipe],
  host: {
    class: 'block min-w-0 overflow-hidden h-full',
  },
})
export class AnalysisTableComponent {
  readonly viewManager = input<GridViewManagerComponent>();

  readonly gridApi = signal<GridApi | null>(null);

  private readonly store = inject(Store);
  protected analysisStateService = inject(AnalysisStateService);
  private readonly translateService = inject(TranslateService);
  private readonly twoDecimalPercent = inject(TwoDecimalPercentPipe);
  private readonly dateUtilsService = inject(DateUtilsService);
  private readonly localeService = inject(LocaleService);

  private readonly analysisNavigationService = inject(
    AnalysisNavigationService,
  );
  private readonly thresholdService = inject(AnalysisThresholdService);

  protected readonly sideBarConfig: SideBarDef = {
    toolPanels: [
      columnsToolPanelDef(this.translateService),
      filtersToolPanelDef(this.translateService),
    ],
  };

  private readonly analysisHierarchy = this.store.selectSignal(
    selectAnalysisHierarchy,
  );

  private readonly analysisDrillDownFilter =
    this.store.selectSignal(selectAnalysisFilter);

  private readonly tableFilterParam = this.store.selectSignal(
    selectAnalysisTableFilterByQueryParam,
  );

  private lastSyncedTableFilter: string | null = null;
  private lastHierarchyKey: string | null = null;

  private readonly isMeasureVisible = createMeasureVisibility(this.store);

  private readonly selectedMeasureColumns = computed(() =>
    getSelectedMeasureColumns(
      this.analysisStateService.selectedConfiguration(),
      this.isMeasureVisible,
      this.translateService.instant('ANALYSIS.ACCURACY_SUFFIX'),
    ),
  );

  protected groups = this.analysisStateService.tableVersionGroups;

  protected nextHierarchies = computed(() => {
    const version = this.analysisStateService.selectedVersion();
    if (!version) {
      return null;
    }
    const hierarchy = this.analysisHierarchy();

    // exception: last level reached
    if (hierarchy.length >= version.hierarchy.length) {
      return null;
    }

    // return current hierarchy and next level
    const nextLevel = hierarchy.length;
    return version.hierarchy.slice(0, nextLevel + 1);
  });

  private readonly analysisContextService = inject(AnalysisContextService);

  private readonly predictionColumnHeader =
    this.analysisContextService.predictionSeriesName;

  private readonly predictionColumnTooltipKey = computed(() => {
    const version = this.analysisStateService.selectedVersion();
    return version?.dataSourceType ===
      AnalysisDataSourceType.ADDITIONAL_TIMESERIES
      ? 'ANALYSIS.PLANNING_TOOLTIP'
      : 'ANALYSIS.FORECAST_TOOLTIP';
  });

  private readonly comparisonTimeseriesNames =
    this.analysisContextService.comparisonSeriesNames;

  private seriesPrediction(
    data: AnalysisVersionGroupResponse | undefined,
    timeseriesName: string | null,
  ): number | null {
    if (!data) {
      return null;
    }
    if (data.rowType === AnalysisRowType.AGGREGATE) {
      if (timeseriesName === null) {
        return data.predictionSum ?? null;
      }
      return (
        data.comparisonTimeseriesNames.find(
          (series) => series.timeseriesName === timeseriesName,
        )?.predictionSum ?? null
      );
    }
    if (timeseriesName === null) {
      return data.prediction ?? null;
    }
    return (
      data.comparisonTimeseriesNames.find(
        (series) => series.timeseriesName === timeseriesName,
      )?.prediction ?? null
    );
  }

  private static seriesTruth(
    data: AnalysisVersionGroupResponse,
  ): number | null {
    return data.rowType === AnalysisRowType.AGGREGATE
      ? (data.truthSum ?? null)
      : (data.truth ?? null);
  }

  private seriesError(
    data: AnalysisVersionGroupResponse | undefined,
    timeseriesName: string | null,
  ): number | null {
    if (!data) {
      return null;
    }
    const prediction = this.seriesPrediction(data, timeseriesName);
    const truth = AnalysisTableComponent.seriesTruth(data);
    if (prediction == null || truth == null) {
      return null;
    }
    return prediction - truth;
  }

  private seriesErrorPercent(
    data: AnalysisVersionGroupResponse | undefined,
    timeseriesName: string | null,
  ): number | null {
    if (!data) {
      return null;
    }
    const truth = AnalysisTableComponent.seriesTruth(data);
    const error = this.seriesError(data, timeseriesName);
    if (error == null || truth == null || truth === 0) {
      return null;
    }
    return error / truth;
  }

  gridOptions: GridOptions<AnalysisVersionGroupResponse> = {
    treeData: true,
    getDataPath: (data: AnalysisVersionGroupResponse) => data.path,
    groupDefaultExpanded: 0,
    isGroupOpenByDefault: () => this.analysisHierarchy().length === 0,
    defaultColDef: {
      tooltipComponent: GridTooltipTranslationComponent,
      flex: 1,
    },
    getRowStyle: (params: RowClassParams<AnalysisVersionGroupResponse>) =>
      // set slightly gray background for timepoint rows
      params.data?.rowType === AnalysisRowType.TIMEPOINT
        ? { backgroundColor: 'var(--surface-neutral-subdued-color)' }
        : undefined,
    onColumnVisible: () => {
      this.gridApi()?.sizeColumnsToFit();
    },
    postSortRows: (
      params: PostSortRowsParams<AnalysisVersionGroupResponse>,
    ) => {
      const rows = params.nodes;
      if (
        rows.length > 0 &&
        rows[0].data?.rowType === AnalysisRowType.TIMEPOINT
      ) {
        rows.sort((a, b) => {
          const dataA = a.data;
          const dataB = b.data;
          const dateA =
            dataA?.rowType === AnalysisRowType.TIMEPOINT ? dataA.tsDate : '';
          const dateB =
            dataB?.rowType === AnalysisRowType.TIMEPOINT ? dataB.tsDate : '';
          return dateB.localeCompare(dateA);
        });
      }
    },
  };

  constructor() {
    effect(() => {
      this.columnDefs();
      untracked(() =>
        this.viewManager()?.reapplyGridViewAfterColumnDefChanges(),
      );
    });

    effect(() => {
      const api = this.gridApi();
      const raw = this.tableFilterParam() ?? null;
      const loaded = this.groups.hasValue();
      if (!api || !loaded) {
        return;
      }
      untracked(() => this.applyFilterModelFromUrl(api, raw));
    });

    effect(() => {
      const key = JSON.stringify([
        this.analysisHierarchy(),
        this.analysisDrillDownFilter() ?? null,
      ]);
      untracked(() => this.clearHierarchyFilterOnLevelChange(key));
    });
  }

  /**
   * Keeps the grid on whatever the URL says, so that back/forward and a shared
   * link land on the same filters. `lastSyncedTableFilter` holds the last value
   * that grid and URL agreed on and breaks the cycle between this method and
   * onFilterChanged, which writes the model back into the URL.
   */
  private applyFilterModelFromUrl(api: GridApi, raw: string | null): void {
    if (raw === this.lastSyncedTableFilter) {
      return;
    }
    this.lastSyncedTableFilter = raw;
    api.setFilterModel(AnalysisTableComponent.parseTableFilterParam(raw));
  }

  // group-column values belong to the level that produced them; numeric filters stay valid
  private clearHierarchyFilterOnLevelChange(key: string): void {
    const previous = this.lastHierarchyKey;
    this.lastHierarchyKey = key;
    if (previous === null || previous === key) {
      return;
    }
    const api = this.gridApi();
    if (!api) {
      return;
    }
    const model = { ...api.getFilterModel() };
    if (!(GROUP_AUTO_COLUMN_ID in model)) {
      return;
    }
    delete model[GROUP_AUTO_COLUMN_ID];
    api.setFilterModel(model);
  }

  protected onFilterChanged(event: FilterChangedEvent): void {
    const api = this.gridApi();
    if (!api) {
      return;
    }
    const model = api.getFilterModel();
    if (
      event.afterDataChange &&
      AnalysisTableComponent.hasEmptySetFilter(model)
    ) {
      const cleaned = { ...model };
      delete cleaned[GROUP_AUTO_COLUMN_ID];
      api.setFilterModel(cleaned);
      return;
    }
    const serialized =
      Object.keys(model).length > 0 ? JSON.stringify(model) : null;
    if (serialized === this.lastSyncedTableFilter) {
      return;
    }
    this.lastSyncedTableFilter = serialized;
    this.analysisNavigationService.setQueryParams({
      tableFilter: serialized,
    });
  }

  /**
   * ag-grid empties a set-filter model when its selected keys are no longer
   * among the available values, and an empty set filters every row out. The
   * group column's values change with the level, so drop the filter instead —
   * but only when the data changed underneath it. An empty selection the user
   * made themselves ("deselect all", then tick one entry) has to survive.
   */
  private static hasEmptySetFilter(model: FilterModel): boolean {
    const groupFilter = model[GROUP_AUTO_COLUMN_ID] as
      | { values?: unknown }
      | undefined;
    return (
      !!groupFilter &&
      Array.isArray(groupFilter.values) &&
      groupFilter.values.length === 0
    );
  }

  private static isRestorableFilterEntry(value: unknown): boolean {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return false;
    }
    const values = (value as { values?: unknown }).values;
    return !(Array.isArray(values) && values.length === 0);
  }

  private static parseTableFilterParam(raw: string | null): FilterModel | null {
    if (!raw) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        return null;
      }
      const entries = Object.entries(parsed as Record<string, unknown>).filter(
        ([, value]) => AnalysisTableComponent.isRestorableFilterEntry(value),
      );
      return entries.length > 0
        ? (Object.fromEntries(entries) as FilterModel)
        : null;
    } catch {
      return null;
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi.set(event.api);
  }

  readonly getMainMenuItems = (
    params: GetMainMenuItemsParams<AnalysisVersionGroupResponse>,
  ) => {
    return createGetMainMenuItems(params, this.viewManager());
  };

  /**
   * Filter key of the group column. Returns null for rows that do not belong to
   * the level currently displayed, so that rows still held by the resource from
   * the previous level cannot collapse every key onto the n/a placeholder — that
   * would make ag-grid drop the selected key and filter every row out.
   * Always a string: dimension values also arrive as number/boolean.
   */
  private groupFilterLabel(
    data: AnalysisVersionGroupResponse,
    hierarchy: string[],
  ): string | null {
    if (data.rowType !== AnalysisRowType.AGGREGATE) {
      return null;
    }
    const level = hierarchy.at(-1);
    if (level === undefined) {
      return null;
    }
    if (!(level in data.groupCombination)) {
      return null;
    }
    const value = data.groupCombination[level];
    return value == null
      ? this.translateService.instant('ANALYSIS.NOT_APPLICABLE')
      : String(value);
  }

  /**
   * Values are taken from the grid rather than supplied: ag-grid then keeps them
   * in sync with the rows and validates the model against them, instead of
   * resetting the selection every time the values are refreshed.
   */
  private readonly groupFilterParams: ISetFilterParams = {
    comparator: alphabeticalCompare,
    valueFormatter: (params: ValueFormatterParams) =>
      (params.value as string | null) ??
      this.translateService.instant('ANALYSIS.NOT_APPLICABLE'),
  };

  private readonly groupFilterValueGetter = (
    params: ValueGetterParams<AnalysisVersionGroupResponse>,
  ): string | null => {
    const hierarchy = this.analysisHierarchy();
    const data = params.data;
    if (data?.rowType === AnalysisRowType.AGGREGATE) {
      return this.groupFilterLabel(data, hierarchy);
    }
    // timepoint rows carry their group's key, so they add no blank entry to the
    // value list and can never keep a filtered-out group alive
    const group = params.node?.parent?.data;
    return group ? this.groupFilterLabel(group, hierarchy) : null;
  };

  protected autoGroupColumnDef = computed(
    (): ColDef<AnalysisVersionGroupResponse> => {
      const hierarchy = this.analysisHierarchy();
      const hasNextHierarchy = !!this.nextHierarchies();
      return {
        headerName: this.determineGroupCombinationHeader(hierarchy),
        field: 'groupCombination',
        suppressColumnsToolPanel: true,
        filter: 'agSetColumnFilter',
        // 'groupCombination' holds an object, so ag-grid would infer the object
        // cellDataType and inject a set-filter valueFormatter that renders every
        // key as the blanks placeholder
        cellDataType: false,
        filterParams: this.groupFilterParams,
        filterValueGetter: this.groupFilterValueGetter,
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: GroupNameCellComponent,
          innerRendererParams: { hasNextHierarchy },
        },
        initialWidth: 250,
        comparator: (_a, _b, nodeA, nodeB) => {
          if (nodeA.key == null) return -1;
          if (nodeB.key == null) return 1;

          const dataA = nodeA.data;
          const dataB = nodeB.data;

          if (
            dataA?.rowType === AnalysisRowType.TIMEPOINT &&
            dataB?.rowType === AnalysisRowType.TIMEPOINT
          ) {
            return dataA.tsDate.localeCompare(dataB.tsDate);
          }

          return nodeA.key.localeCompare(nodeB.key);
        },
        cellStyle: (params: RowClassParams<AnalysisVersionGroupResponse>) =>
          // only apply pointer cursor style to aggregate rows with further hierarchies
          params.data?.rowType === AnalysisRowType.AGGREGATE &&
          this.nextHierarchies()
            ? { cursor: 'pointer' }
            : null,
        valueFormatter: (params: { data?: AnalysisVersionGroupResponse }) => {
          const data = params.data;
          if (!data) {
            return '';
          }
          if (data.rowType === AnalysisRowType.AGGREGATE) {
            return this.determineGroupCombinationCellValue(data, hierarchy);
          }
          if (data.rowType === AnalysisRowType.TIMEPOINT) {
            return this.dateUtilsService.formatMonthYearBasedOnLanguage(
              data.tsDate,
            );
          }
          return '';
        },
      };
    },
  );

  rowData: Signal<AnalysisVersionGroupResponse[]> = computed(() => {
    if (this.analysisStateService.hasNoElapsedMonths()) {
      return [];
    }
    return this.groups.hasValue() ? this.groups.value() : [];
  });

  protected readonly noRowsOverlay = PmNoRowsOverlayComponent;
  protected readonly noRowsOverlayParams = {
    translationKey: 'ANALYSIS.NO_ELAPSED_MONTHS_EMPTY_STATE',
  };

  private static readonly DASH_PLACEHOLDER = '\u2013';

  private static aggregateError(data: AnalysisAggregateRow): number | null {
    if (data.predictionSum == null || data.truthSum == null) {
      return null;
    }
    return data.predictionSum - data.truthSum;
  }

  private readonly numberFilterParams = createNumberFilterParams(
    this.localeService,
  );

  private numericFilter(
    getValue: (data: AnalysisAggregateRow) => number | null | undefined,
    scale = 1,
  ) {
    return numericFilterColDef<
      AnalysisVersionGroupResponse,
      AnalysisAggregateRow
    >({
      filterParams: this.numberFilterParams,
      scale,
      isFilterable: AnalysisTableComponent.isAggregateRow,
      getValue,
    });
  }

  private static isAggregateRow(
    data: AnalysisVersionGroupResponse,
  ): data is AnalysisAggregateRow {
    return data.rowType === AnalysisRowType.AGGREGATE;
  }

  private formatNumberOrDash(value: number | null | undefined): string {
    return value == null
      ? AnalysisTableComponent.DASH_PLACEHOLDER
      : formatValueAsNumber(value);
  }

  private formatPercentOrDash(
    value: number | string | null | undefined,
  ): string {
    if (value == null) {
      return AnalysisTableComponent.DASH_PLACEHOLDER;
    }
    const formatted = this.twoDecimalPercent.transform(value);
    return formatted || AnalysisTableComponent.DASH_PLACEHOLDER;
  }

  private static readonly CELL_ALIGN_RIGHT = { textAlign: 'right' } as const;

  private static readonly SERIES_HEADER_STYLES: readonly HeaderStyle[] = [
    { backgroundColor: 'var(--surface-neutral-color)' },
    { backgroundColor: 'var(--surface-neutral-subdued-color)' },
  ];

  private static seriesColId(
    colId: string,
    timeseriesName: string | null,
  ): string {
    return timeseriesName === null ? colId : `${colId}:${timeseriesName}`;
  }

  private static seriesMeasure(
    data: AnalysisAggregateRow,
    measure: Measure,
    timeseriesName: string | null,
  ): number | null {
    if (timeseriesName === null) {
      return data[measure];
    }
    return (
      data.comparisonTimeseriesNames.find(
        (series) => series.timeseriesName === timeseriesName,
      )?.[measure] ?? null
    );
  }

  private measureColumns(
    timeseriesName: string | null,
  ): ColDef<AnalysisVersionGroupResponse>[] {
    const seriesValue = (
      data: AnalysisAggregateRow,
      column: SelectedMeasureColumn,
    ) => {
      const value = AnalysisTableComponent.seriesMeasure(
        data,
        column.measure,
        timeseriesName,
      );
      return column.isAccuracy ? toAccuracyValue(value) : value;
    };

    return this.selectedMeasureColumns().map((column) => ({
      colId: AnalysisTableComponent.seriesColId(column.colId, timeseriesName),
      headerName: column.header,
      headerTooltip: column.isAccuracy
        ? undefined
        : getMeasureTooltipKey(column.measure),
      valueGetter: (params) => {
        const data = params.data;
        if (data?.rowType !== AnalysisRowType.AGGREGATE) {
          return null;
        }
        return seriesValue(data, column);
      },
      valueFormatter: (params) => {
        if (params.data?.rowType === AnalysisRowType.TIMEPOINT) {
          return AnalysisTableComponent.DASH_PLACEHOLDER;
        }
        return column.format === 'number'
          ? this.formatNumberOrDash(params.value)
          : this.formatPercentOrDash(params.value);
      },
      cellStyle: (params) => ({
        ...AnalysisTableComponent.CELL_ALIGN_RIGHT,
        ...(column.isAccuracy
          ? null
          : this.thresholdService.getCellStyle(column.measure, params.value)),
      }),
      ...this.numericFilter(
        (data) => seriesValue(data, column),
        column.format === 'percent' ? 100 : 1,
      ),
    }));
  }

  private valueColumnsOf(
    timeseriesName: string | null,
  ): ColDef<AnalysisVersionGroupResponse>[] {
    const colId = (id: string) =>
      AnalysisTableComponent.seriesColId(id, timeseriesName);

    return [
      {
        colId: colId('prediction'),
        headerName: this.translateService.instant('ANALYSIS.VALUE'),
        headerTooltip:
          timeseriesName === null
            ? this.predictionColumnTooltipKey()
            : 'ANALYSIS.PLANNING_TOOLTIP',
        valueGetter: (params) =>
          this.seriesPrediction(params.data, timeseriesName),
        valueFormatter: (params) => this.formatNumberOrDash(params.value),
        cellStyle: () => AnalysisTableComponent.CELL_ALIGN_RIGHT,
        ...this.numericFilter((data) =>
          this.seriesPrediction(data, timeseriesName),
        ),
      },
      {
        colId: colId('error'),
        headerName: this.translateService.instant('ANALYSIS.ERROR'),
        headerTooltip: 'ANALYSIS.ERROR_TOOLTIP',
        valueGetter: (params) => this.seriesError(params.data, timeseriesName),
        valueFormatter: (params) => this.formatNumberOrDash(params.value),
        cellStyle: () => AnalysisTableComponent.CELL_ALIGN_RIGHT,
        ...this.numericFilter((data) => this.seriesError(data, timeseriesName)),
      },
      {
        colId: colId('errorPercent'),
        headerName: this.translateService.instant('ANALYSIS.ERROR_PERCENT'),
        headerTooltip: 'ANALYSIS.ERROR_PERCENT_TOOLTIP',
        valueGetter: (params) =>
          this.seriesErrorPercent(params.data, timeseriesName),
        valueFormatter: (params) => this.formatPercentOrDash(params.value),
        cellStyle: (params) => ({
          ...AnalysisTableComponent.CELL_ALIGN_RIGHT,
          ...this.thresholdService.getCellStyle(Measure.Mpe, params.value),
        }),
        ...this.numericFilter(
          (data) => this.seriesErrorPercent(data, timeseriesName),
          100,
        ),
      },
      ...this.measureColumns(timeseriesName),
    ];
  }

  /** neighbouring groups alternate between the two shades, so their columns stay apart */
  private seriesColumnGroup(
    timeseriesName: string | null,
    index: number,
  ): ColGroupDef<AnalysisVersionGroupResponse> {
    const headerStyle =
      AnalysisTableComponent.SERIES_HEADER_STYLES[
        index % AnalysisTableComponent.SERIES_HEADER_STYLES.length
      ];

    return {
      groupId: timeseriesName ?? 'forecast',
      headerName: timeseriesName ?? this.predictionColumnHeader(),
      headerStyle,
      marryChildren: true,
      children: this.valueColumnsOf(timeseriesName).map((column) => ({
        ...column,
        headerStyle,
      })),
    };
  }

  protected columnDefs: Signal<
    (
      | ColDef<AnalysisVersionGroupResponse>
      | ColGroupDef<AnalysisVersionGroupResponse>
    )[]
  > = computed(() => {
    const comparisonTimeseriesNames = this.comparisonTimeseriesNames();

    const actualColumn: ColDef<AnalysisVersionGroupResponse> = {
      colId: 'actual',
      headerName: this.translateService.instant('ANALYSIS.ACTUAL'),
      headerTooltip: 'ANALYSIS.ACTUAL_TOOLTIP',
      sort: 'desc' as const,
      valueGetter: (params) => {
        const data = params.data;
        if (!data) return null;
        return AnalysisTableComponent.seriesTruth(data);
      },
      valueFormatter: (params) => this.formatNumberOrDash(params.value),
      cellStyle: () => AnalysisTableComponent.CELL_ALIGN_RIGHT,
      ...this.numericFilter((data) => data.truthSum),
    };

    const forecastVersionColumn: ColDef<AnalysisVersionGroupResponse> = {
      colId: 'forecastVersion',
      filter: false,
      headerName: this.translateService.instant(
        'RESULT.KPIS.EXPORT_OPTIONS.XLSX_METADATA.FORECAST_VERSION',
      ),
      cellRenderer: OpenForecastVersionCellComponent,
    };

    return [
      actualColumn,
      this.seriesColumnGroup(null, 0),
      ...comparisonTimeseriesNames.map((timeseriesName, index) =>
        this.seriesColumnGroup(timeseriesName, index + 1),
      ),
      forecastVersionColumn,
    ];
  });

  handleCellClick(event: CellClickedEvent<AnalysisVersionGroupResponse>) {
    if (event.data === undefined) {
      console.error('No data in clicked cell');
      return;
    }

    if (event.data.rowType !== AnalysisRowType.AGGREGATE) {
      return;
    }

    const nextHierarchies = this.nextHierarchies();

    if (!nextHierarchies) {
      console.warn('No further hierarchy levels to navigate to');
      return;
    }

    if (event.colDef.field === 'groupCombination') {
      this.analysisNavigationService.setQueryParams({
        hierarchy: this.nextHierarchies(),
        filter: event.data.groupCombination,
      });
    }
  }

  private determineGroupCombinationHeader(hierarchy: string[]): string {
    return hierarchy.at(-1) ?? this.translateService.instant('ANALYSIS.TOTAL');
  }

  private determineGroupCombinationCellValue(
    data: AnalysisAggregateRow,
    hierarchy: string[],
  ): string {
    if (hierarchy.length < 1) {
      return this.translateService.instant('ANALYSIS.TOTAL');
    }
    const latestHierarchyLevel = hierarchy.at(-1) as string;

    return (
      data.groupCombination[latestHierarchyLevel] ??
      this.translateService.instant('ANALYSIS.NOT_APPLICABLE')
    );
  }
}
